import axios from 'axios';
import { getConfig } from '../config';
import { AdminApiDomain, getAdminApiUrl } from '../config/ApiRouting';
import type {
  RecommendationLabInputOptions,
  RecommendationLabPreviewRequest,
  RecommendationLabPreviewResult,
} from '../models/RecommendationLab';
import type {
  RecommendationPromptCatalog,
  RecommendationPromptDraft,
  SaveRecommendationPromptDraftRequest,
} from '@/models/RecommendationLabPrompts';

export * from '../models/RecommendationLab';

export class RecommendationLabClient {
  private static readonly PromptSandboxTimeoutMs = 15_000;
  private readonly baseUrl: string;
  private readonly headers: Record<string, string>;

  constructor() {
    this.baseUrl = getAdminApiUrl(AdminApiDomain.RECOMMENDATION_LAB);
    this.headers = {
      'X-API-KEY': getConfig().apiKey,
    };
  }

  async getInputOptions(): Promise<RecommendationLabInputOptions> {
    const response = await axios.get<RecommendationLabInputOptions>(
      `${this.baseUrl}/skinAnalysis/admin/recommendations/inputOptions`,
      {
        headers: this.headers,
      },
    );
    return response.data;
  }

  async preview(request: RecommendationLabPreviewRequest): Promise<RecommendationLabPreviewResult> {
    const { mode, promptDraft, ...payload } = request;
    const route = mode === 'client_parity' ? 'clientPreview' : 'preview';
    const response = await axios.post<RecommendationLabPreviewResult>(
      `${this.baseUrl}/skinAnalysis/admin/recommendations/${route}`,
      payload,
      {
        headers: {
          ...this.headers,
          'Content-Type': 'application/json',
          ...(promptDraft
            ? {
                'X-Skin-Analysis-Prompt-Draft-Id': promptDraft.draftId,
                'X-Skin-Analysis-Prompt-Draft-Revision': String(promptDraft.revision),
              }
            : {}),
        },
        // The dry-run walks Vertex + LLM per step; allow more than the axios default.
        timeout: 60_000,
      },
    );
    return response.data;
  }

  async getPromptCatalog(): Promise<RecommendationPromptCatalog> {
    const response = await axios.get<RecommendationPromptCatalog>(
      `${this.baseUrl}/skinAnalysis/admin/promptSandbox/catalog`,
      { headers: this.headers, timeout: RecommendationLabClient.PromptSandboxTimeoutMs },
    );
    return response.data;
  }

  async getPromptDraft(draftId: string): Promise<RecommendationPromptDraft> {
    const response = await axios.get<RecommendationPromptDraft>(
      `${this.baseUrl}/skinAnalysis/admin/promptSandbox/drafts/${encodeURIComponent(draftId)}`,
      { headers: this.headers, timeout: RecommendationLabClient.PromptSandboxTimeoutMs },
    );
    return response.data;
  }

  async savePromptDraft(
    draftId: string,
    request: SaveRecommendationPromptDraftRequest,
  ): Promise<RecommendationPromptDraft> {
    const response = await axios.put<RecommendationPromptDraft>(
      `${this.baseUrl}/skinAnalysis/admin/promptSandbox/drafts/${encodeURIComponent(draftId)}`,
      request,
      {
        headers: { ...this.headers, 'Content-Type': 'application/json' },
        timeout: RecommendationLabClient.PromptSandboxTimeoutMs,
      },
    );
    return response.data;
  }

  async deletePromptDraft(draftId: string, expectedRevision: number): Promise<void> {
    await axios.delete(`${this.baseUrl}/skinAnalysis/admin/promptSandbox/drafts/${encodeURIComponent(draftId)}`, {
      headers: this.headers,
      params: { expectedRevision },
      timeout: RecommendationLabClient.PromptSandboxTimeoutMs,
    });
  }
}
